import axios from 'axios';
import _ from 'lodash';
import {
  createContext,
  FC,
  PropsWithChildren,
  useEffect,
  useState,
} from 'react';
import toast from 'react-hot-toast';
import Error404 from '../error/Error404/Error404';
import Auth from '../interfaces/Auth';
import User from '../interfaces/User';
import * as keycloak from '../keycloak';
import { isPublicRoute } from '../services/auth';
import { getUser } from '../services/user';
import CasaLoader from '../shared/components/CasaLoader/CasaLoader';
import { UserProvider } from './User';
import AccessError from '../error/AccessError/AccessError';

const AuthContext = createContext<Auth>({
  currentUser: null,
});

export const AuthProvider: FC<PropsWithChildren> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isRouteAuthenticated, setIsRouteAuthenticated] = useState(false);

  useEffect(() => {
    setIsLoading(true);

    // Public routes render without Keycloak. Returning here also means the axios
    // interceptors below are never installed, so no bearer token is attached and a
    // 401 can't bounce an anonymous visitor to the login page.
    if (isPublicRoute(window.location.pathname)) {
      setIsRouteAuthenticated(true);
      setIsLoading(false);
      return;
    }

    (async () => {
      try {
        const isAuthenticated = await keycloak.initialize();

        if (!isAuthenticated) return keycloak.redirectToLogin();

        const accessToken = keycloak.getAccessToken();

        if (!accessToken) {
          throw new Error('Authentication failed');
        }

        axios.defaults.headers.common[
          'Authorization'
        ] = `Bearer ${accessToken}`;

        // Axios Interceptors for Automatic Token Refresh
        // 1. Request interceptor: proactively update token if close to expiry
        axios.interceptors.request.use(
          async (config) => {
            try {
              // Update token if it expires in less than 30 seconds
              await keycloak.updateToken(30);
              const token = keycloak.getAccessToken();
              if (token) {
                config.headers.Authorization = `Bearer ${token}`;
              }
            } catch (err) {
              console.error('Proactive token refresh failed:', err);
            }
            return config;
          },
          (error) => Promise.reject(error)
        );

        // 2. Response interceptor: reactively update token on 401 invalid_token and retry
        axios.interceptors.response.use(
          (response) => response,
          async (error) => {
            const originalRequest = error.config;
            if (
              error.response &&
              error.response.status === 401 &&
              !originalRequest._retry
            ) {
              // Any 401 is worth one refresh attempt. Matching only on
              // `invalid_token` missed the unauthenticated case, whose body is
              // `{"message":"Access Token is required"}` — that fell through to
              // a rejected promise and rendered the 404 "no permissions" screen
              // instead of sending the user to log in.
              originalRequest._retry = true;
              try {
                // Force refresh by passing -1
                await keycloak.updateToken(-1);
                const token = keycloak.getAccessToken();
                if (token) {
                  axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
                  originalRequest.headers.Authorization = `Bearer ${token}`;
                  return axios(originalRequest);
                }
                // Refresh succeeded but produced no token: the session is
                // unusable either way, so treat it as a failed refresh.
                keycloak.redirectToLogin();
              } catch (refreshErr) {
                console.error('Reactive token refresh failed:', refreshErr);
                keycloak.redirectToLogin();
              }
            }
            return Promise.reject(error);
          }
        );

        // axios.defaults.baseURL = config.ruleServerUrl; // TODO: Need to switch everything api Server
        const user = await getUser();
        setCurrentUser(user);

        toast.success(`Welcome back, ${_.capitalize(user._internalUserEmail||'User')} `);
      } catch (error) {
        console.log("error", error);
        setError((error as any).message);
        toast.error(`Oops! Something went wrong`);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  if (isLoading) return <CasaLoader />;
  // Must precede the guards below — a public visitor has no currentUser and would
  // otherwise be shown a 404 page.
  if (isRouteAuthenticated) return <>{children}</>;
  if (error) return   <Error404 />;
  if (!currentUser)
    return <Error404 />;
  if (!currentUser._isInternalUser) return <AccessError />

  return (
    <AuthContext.Provider value={{ currentUser }}>
      <UserProvider currentUser={currentUser}>{children}</UserProvider>
    </AuthContext.Provider>
  );
};

export default AuthContext;
